import React, { JSX, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';

import { InitialAspectRatioOptionsEnum } from '@/types/images.types';
import { useAppSelector } from '@/store/hooks';
import { getWorkingProject } from '@/store/slices/project/selectors';
import OptionalComponent from '@ui/optional-component/optional-component';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';

import styles from './image-cropper.module.css';
import Button from '@ui/buttons/button/button';

interface Props {
  originalImage: string;
  onFinished: (image: File) => void;
  setCropping?: (inProcess: boolean) => void;
  disableEditingAspectRatio?: boolean;
  initialAspectRatio?: InitialAspectRatioOptionsEnum;
}

const RatioIcon = ({ aspect }: { aspect: AspectRatio }): JSX.Element => {
  const ratio = aspect.w / aspect.h;
  const iconWidth = ratio > 1 ? 12 : 12 * ratio;
  const iconHeight = ratio > 1 ? 12 / ratio : 12;
  return <div className={styles['ratio-icon']} style={{ width: `${iconWidth}px`, height: `${iconHeight}px` }} />;
};

const base64ToFile = async (dataurl: string, filename: string, mimetype: string): Promise<File> => {
  try {
    const res = await fetch(dataurl);
    const buf = await res.arrayBuffer();
    return new File([buf], filename, { type: mimetype });
  } catch (error) {
    console.error(error);
    return new File([], '');
  }
};

const containerWidth = 560;
const containerHeight = 400;
const imgContainerPadding = 20;
// hide image during reset until actual size and position is calculated
const defaultPosition = { left: 0, top: 0, visibility: 'hidden' };
const defaultDraggingCoords = { mouseX: 0, mouseY: 0, imgLeft: 0, imgTop: 0 };

interface AspectRatio {
  text: string;
  w: number;
  h: number;
}
// if aspect ratio (w / h) is greater than container ratio (560 / 400), w = 1, else h =1
// w and h are rounded to the nearest 100th
const aspectRatioList: Array<AspectRatio> = [
  { text: '1:1', w: 1, h: 1 },
  { text: '10:3', w: 1, h: 0.3 },
  { text: '5:9', w: 0.56, h: 1 },
  { text: '3:2', w: 1, h: 0.67 },
  { text: '4:3', w: 1.33, h: 1 },
  { text: '16:9', w: 1, h: 0.56 },
];

interface dimensions {
  top: number;
  left: number;
  height?: number;
  width?: number;
}
const ImageCropper = ({
  originalImage,
  onFinished,
  setCropping,
  disableEditingAspectRatio = false,
  initialAspectRatio,
}: Props) => {
  const workingProject = useAppSelector(getWorkingProject);

  const [activeAspect, setActiveAspect] = useState<AspectRatio>(
    initialAspectRatio ? aspectRatioList[initialAspectRatio] : aspectRatioList[0]
  );
  const [imgDimensions, setImgDimensions] = useState<dimensions>(defaultPosition); // can't start with a predefined width or height
  const [startingImgDimensions, setStartingImgDimensions] = useState<dimensions>(defaultPosition);
  const [isScaling, setIsScaling] = useState(false);
  const [scale, setScale] = useState(1);

  const [isDragging, setIsDragging] = useState(false);
  const [draggingCoords, setDraggingCoords] = useState(defaultDraggingCoords);

  const image = useRef<HTMLImageElement>(null);
  const container = useRef<HTMLDivElement | null>(null);

  const boxDimensions = useMemo(() => {
    const widerBox = activeAspect.w / activeAspect.h > containerWidth / containerHeight;
    const fitBox = widerBox ? containerWidth - imgContainerPadding * 2 : containerHeight - imgContainerPadding * 2;
    const calcWidth = fitBox * activeAspect.w;
    const calcHeight = fitBox * activeAspect.h;
    return {
      width: calcWidth,
      height: calcHeight,
      top: (containerHeight - calcHeight) / 2,
      left: (containerWidth - calcWidth) / 2,
    };
  }, [activeAspect]);

  // previous height and width must be cleared before new image is loaded
  useEffect(() => {
    setCropping?.(true);
    setImgDimensions(defaultPosition);
    setStartingImgDimensions(defaultPosition);
  }, [originalImage, setCropping]);

  useEffect(() => {
    if (isScaling || isDragging) {
      setCropping?.(true);
    }
  }, [setCropping, isScaling, isDragging]);

  const aspectTimeout = useRef<NodeJS.Timeout | null>(null);
  const handleAspect = () => {
    if (aspectTimeout.current) {
      clearTimeout(aspectTimeout.current);
    }
    aspectTimeout.current = setTimeout(() => {
      generateImage();
    }, 200);
  };

  function handleMouseDown(e: React.MouseEvent) {
    setDraggingCoords({
      mouseX: e.clientX,
      mouseY: e.clientY,
      imgLeft: imgDimensions.left,
      imgTop: imgDimensions.top,
    });
    setIsDragging(true);
    setCropping?.(true);
  }

  function handleMouseUp(e: React.MouseEvent) {
    setDraggingCoords({
      mouseX: e.clientX,
      mouseY: e.clientY,
      imgLeft: imgDimensions.left,
      imgTop: imgDimensions.top,
    });
    setIsDragging(false);
    generateImage();
  }

  function handleMouseMove(e: React.MouseEvent) {
    if (isDragging) {
      const deltaX = Math.floor(e.clientX - draggingCoords.mouseX);
      const deltaY = Math.floor(e.clientY - draggingCoords.mouseY);
      const newX = Math.floor(draggingCoords.imgLeft + deltaX);
      const newY = Math.floor(draggingCoords.imgTop + deltaY);

      setImgDimensions({
        ...imgDimensions,
        top: newY,
        left: newX,
      });
    }
  }

  const scaleTimeout = useRef<NodeJS.Timeout | null>(null);
  const handleScale = () => {
    setIsScaling(false);
    if (scaleTimeout.current) {
      clearTimeout(scaleTimeout.current);
    }
    scaleTimeout.current = setTimeout(() => {
      generateImage();
    }, 200);
  };

  function resizeImg(e: React.ChangeEvent<HTMLInputElement>) {
    if (!startingImgDimensions) return;
    const multiple = e.target.valueAsNumber;
    setScale(multiple);

    if (startingImgDimensions?.height && startingImgDimensions.width) {
      setImgDimensions({
        ...imgDimensions,
        height: startingImgDimensions.height * multiple,
        width: startingImgDimensions.width * multiple,
      });
    }
  }

  const imageTimeout = useRef<NodeJS.Timeout | null>(null);
  function handleImageLoad(event: React.SyntheticEvent<HTMLImageElement>) {
    const target = event.target as HTMLImageElement;
    if (!container.current || !target) return;
    setCropping?.(true);
    const containerRatio = containerWidth / containerHeight;
    const imageRatio = target.width / target.height;

    const fitImage =
      imageRatio > containerRatio
        ? {
            top: (containerHeight - containerWidth / imageRatio) / 2,
            left: 0,
            width: containerWidth,
            height: containerWidth / imageRatio,
            visibility: 'visible',
          }
        : {
            top: 0,
            left: (containerWidth - containerHeight * imageRatio) / 2,
            width: containerHeight * imageRatio,
            height: containerHeight,
            visibility: 'visible',
          };

    setImgDimensions(fitImage);
    setStartingImgDimensions(fitImage);
    if (imageTimeout.current) {
      clearTimeout(imageTimeout.current);
    }
    imageTimeout.current = setTimeout(() => {
      generateImage();
    }, 200);
  }

  async function generateImage() {
    setCropping?.(true);
    if (image.current && workingProject) {
      const canvas = document.createElement('canvas');
      canvas.style.position = 'fixed';
      canvas.style.zIndex = '2';
      canvas.style.bottom = '0';

      const pixelRatio = image.current.naturalWidth / image.current.width;

      canvas.width = Math.floor(boxDimensions.width * pixelRatio * scale);
      canvas.height = Math.floor(boxDimensions.height * pixelRatio * scale);

      const canvasLeft = boxDimensions.left - imgDimensions.left;
      const canvasTop = boxDimensions.top - imgDimensions.top;

      const interpLeft = Math.floor(pixelRatio * canvasLeft);
      const interpTop = Math.floor(pixelRatio * canvasTop);

      const ctx = canvas.getContext('2d');
      ctx?.scale(scale, scale);
      ctx?.drawImage(image.current, -interpLeft, -interpTop);

      // only use blob method for pngs (which might need transparency) because it dramatically increases file size
      if (originalImage.endsWith('png')) {
        canvas.toBlob(
          (blob: Blob | null) => {
            if (blob) {
              onFinished(new File([blob], workingProject.name.replace(/[^a-zA-Z0-9]/g, '-') + '.png'));
            }
          },
          'image/png',
          0.7
        );
      } else {
        const img = canvas.toDataURL('image/jpeg', 0.9);
        const file = await base64ToFile(img, workingProject.name.replace(/[^a-zA-Z0-9]/g, '-') + '.jpg', 'image/jpeg');
        onFinished(file);
      }
      if (!isDragging && !isScaling) setCropping?.(false);
    }
  }

  return (
    <div className={styles['image-editor']}>
      <OptionalComponent display={!disableEditingAspectRatio}>
        <div className={styles['aspect-selector']} onMouseLeave={handleAspect}>
          {aspectRatioList.map((aspect) => (
            <Button
              border="2px solid transparent"
              key={aspect.text}
              hoverBackgroundColor="var(--gray-700)"
              className={classnames('flex fd-row jc-center ai-center', {
                [styles.active]: activeAspect.text === aspect.text,
              })}
              onClick={() => setActiveAspect(aspect)}
            >
              <RatioIcon aspect={aspect} />
              <span>{aspect.text}</span>
            </Button>
          ))}
        </div>
      </OptionalComponent>
      <div
        ref={container}
        className={styles['image-container']}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <img
          style={imgDimensions}
          ref={image}
          crossOrigin="anonymous"
          src={originalImage}
          alt={'uploaded image'}
          onLoad={handleImageLoad}
        />
        <div style={boxDimensions} className={styles['image-window-indicator']}>
          <div className={`${styles['drag-icon-container']} ${isDragging ? styles.dragging : ''}`}>
            <Icon name={ICONS.DRAG_ARROWS} size={12} color="var(--gray-100)" />
            <p>Drag to Reposition</p>
          </div>
        </div>
      </div>
      <div className={styles['zoom-slider']} onMouseDown={() => setIsScaling(true)} onMouseOut={handleScale}>
        <Icon name={ICONS.ZOOM_OUT} size={14} color="var(--gray-400)" />
        <input type="range" min={0.8} max={3} value={scale} step={0.05} onChange={resizeImg} />
        <Icon name={ICONS.ZOOM_IN} size={14} color="var(--gray-400)" />
      </div>
    </div>
  );
};

export default ImageCropper;
